<template>
  <div class="auth-debug">
    <h3>Authentication Debug</h3>
    
    <div class="debug-section">
      <h4>Current Authentication State</h4>
      <div class="debug-info">
        <div><strong>Role:</strong> {{ userRole }}</div>
        <div><strong>Subscription Key:</strong> {{ subscriptionKey }}</div>
        <div><strong>Can View:</strong> {{ canView }}</div>
        <div><strong>Can Edit:</strong> {{ canEdit }}</div>
      </div>
    </div>

    <div class="debug-section">
      <h4>Available Keys</h4>
      <div class="keys-list">
        <div 
          v-for="key in availableKeys" 
          :key="key.key"
          class="key-item"
          :class="{ active: key.key === subscriptionKey }"
        >
          <Badge :variant="key.role === 'Contributor' ? 'success' : 'info'">
            {{ key.role }}
          </Badge>
          <span>{{ key.description }}</span>
          <code>{{ key.key }}</code>
        </div>
      </div>
    </div>

    <div class="debug-section">
      <h4>Test API Call</h4>
      <Button @click="testTenantApi" :disabled="loading">
        Test Tenant API
      </Button>
      
      <div v-if="loading" class="loading">
        <ProgressBar :indeterminate="true" />
        <span>Loading tenants...</span>
      </div>
      
      <div v-if="apiResult" class="api-result">
        <h5>API Result:</h5>
        <pre>{{ JSON.stringify(apiResult, null, 2) }}</pre>
      </div>
      
      <div v-if="apiError" class="api-error">
        <h5>API Error:</h5>
        <pre>{{ apiError }}</pre>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { configurationService } from '@/services/api'
import { withApiNotifications, API_MESSAGES } from '@/utils/api-notifications'
import Badge from '@/components/atoms/Badge.vue'
import Button from '@/components/atoms/Button.vue'
import ProgressBar from '@/components/atoms/ProgressBar.vue'

const { userRole, subscriptionKey, canView, canEdit, availableKeys } = useAuth()

const loading = ref(false)
const apiResult = ref<any>(null)
const apiError = ref<string | null>(null)

async function testTenantApi() {
  loading.value = true
  apiResult.value = null
  apiError.value = null
  
  try {
    await withApiNotifications(
      async () => {
        const tenants = await configurationService.getTenants()
        apiResult.value = tenants
        return tenants
      },
      {
        loadingMessage: API_MESSAGES.LOADING.GET_TENANTS,
        successMessage: `${apiResult.value?.length || 0} tenants loaded successfully`,
        errorMessage: API_MESSAGES.ERROR.GET_TENANTS,
        notificationOptions: {
          source: { id: 'auth-debug', label: 'Auth Debug' }
        }
      }
    )
  } catch (error: any) {
    apiError.value = error.message || 'Unknown error occurred'
    console.error('API Error:', error)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped lang="scss">
.auth-debug {
  padding: 16px;
  max-width: 800px;
  
  h3 {
    margin: 0 0 24px 0;
    color: var(--vscode-editor-foreground);
  }
  
  h4 {
    margin: 0 0 12px 0;
    color: var(--vscode-editor-foreground);
    font-size: 14px;
  }
  
  h5 {
    margin: 12px 0 8px 0;
    color: var(--vscode-editor-foreground);
    font-size: 13px;
  }
}

.debug-section {
  margin-bottom: 24px;
  padding: 16px;
  background: var(--vscode-editorWidget-background);
  border: 1px solid var(--vscode-widget-border);
  border-radius: 4px;
}

.debug-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 13px;
}

.keys-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.key-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--vscode-editorGroup-background);
  border-radius: 4px;
  border: 1px solid transparent;
  
  &.active {
    border-color: var(--vscode-focusBorder);
    background: var(--vscode-list-activeSelectionBackground);
  }
  
  code {
    margin-left: auto;
    padding: 4px 8px;
    background: var(--vscode-textCodeBlock-background);
    border-radius: 3px;
    font-size: 12px;
  }
}

.loading {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  
  span {
    font-size: 13px;
    color: var(--vscode-descriptionForeground);
  }
}

.api-result, .api-error {
  margin-top: 12px;
  
  pre {
    background: var(--vscode-textCodeBlock-background);
    border: 1px solid var(--vscode-widget-border);
    border-radius: 4px;
    padding: 12px;
    overflow-x: auto;
    font-size: 12px;
    line-height: 1.4;
  }
}

.api-error pre {
  background: var(--vscode-inputValidation-errorBackground);
  border-color: var(--vscode-inputValidation-errorBorder);
  color: var(--vscode-inputValidation-errorForeground);
}
</style>
